import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchStageInfo, fetchStages } from "../data/stages.js";
import {
  buildReuseChart,
  buildTimelineRowFromFlights,
  extractFlightDates,
  statusIsActive,
} from "../graphComponents/reuseTimeline.js";
import { ReuseTimelinePanel } from "./fleetPanels.js";
import BoosterCompare, { pickDefaultBoosters } from "./BoosterCompare.js";
import "./vehicleList.css";
import "./BoosterStats.css";

const PAGE_COPY = {
  kicker: "Falcon fleet",
  title: "Booster Compare",
  blurb: "Head-to-head flights, payload, turnaround, and cadence — pick up to four boosters.",
  timelineLabel: "Comparison timeline",
  timelineTitle: "Selected boosters",
  detailPath: "stage",
  boostersHref: "/falcon/boosters/",
  statsHref: "/falcon/stats/?tab=boosters",
};

const sameId = (a, b) => String(a) === String(b);

const BoosterComparePage = () => {
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reuseTimeline, setReuseTimeline] = useState([]);
  const [reuseLoading, setReuseLoading] = useState(false);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [reuseView, setReuseView] = useState("relative");
  const timelineCacheKey = useRef("");
  const selectionSeededRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setFleetLoading(true);
      try {
        const stageData = await fetchStages("", null, "falcon", "BOOSTER");
        const fleet = stageData.stages || [];
        if (cancelled) {
          return;
        }

        setFleetVehicles(fleet);

        if (!selectionSeededRef.current && fleet.length) {
          const defaults = pickDefaultBoosters(fleet);
          selectionSeededRef.current = true;
          setSelectedIds(defaults);
          defaults.forEach((id) => {
            fetchStageInfo(id).catch(() => {});
          });
        }
      } catch (error) {
        if (!cancelled) {
          setFleetVehicles([]);
        }
      } finally {
        if (!cancelled) {
          setFleetLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelectedIdsChange = useCallback((next) => {
    setSelectedIds((current) => (typeof next === "function" ? next(current) : next));
  }, []);

  const timelineVehicles = useMemo(
    () =>
      selectedIds
        .map((id) => fleetVehicles.find((vehicle) => sameId(vehicle.id, id)))
        .filter(Boolean),
    [selectedIds, fleetVehicles]
  );

  useEffect(() => {
    if (!timelineVehicles.length) {
      setReuseTimeline([]);
      timelineCacheKey.current = "";
      return undefined;
    }

    const cacheKey = timelineVehicles.map((vehicle) => vehicle.id).join(",");
    if (cacheKey === timelineCacheKey.current) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setReuseLoading(true);
      try {
        const details = await Promise.all(
          timelineVehicles.map(async (vehicle) => {
            try {
              const detail = await fetchStageInfo(vehicle.id);
              const flights = extractFlightDates(detail.stage_and_recoveries);
              return buildTimelineRowFromFlights({
                id: vehicle.id,
                name: vehicle.name,
                active: statusIsActive(vehicle.status),
                flights,
                count: Number(vehicle.num_launches || flights.length),
              });
            } catch (error) {
              return null;
            }
          })
        );

        if (!cancelled) {
          timelineCacheKey.current = cacheKey;
          const byId = new Map(details.filter(Boolean).map((row) => [String(row.id), row]));
          setReuseTimeline(timelineVehicles.map((vehicle) => byId.get(String(vehicle.id))).filter(Boolean));
        }
      } catch (error) {
        if (!cancelled) {
          setReuseTimeline([]);
        }
      } finally {
        if (!cancelled) {
          setReuseLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [timelineVehicles]);

  const reuseChart = useMemo(() => buildReuseChart(reuseTimeline, reuseView), [reuseTimeline, reuseView]);

  const timelineCopy = useMemo(() => {
    const names = timelineVehicles.map((vehicle) => vehicle.name).filter(Boolean);
    const label =
      names.length <= 2 ? names.join(" vs ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;

    return {
      ...PAGE_COPY,
      timelineTitle: names.length ? label : PAGE_COPY.timelineTitle,
    };
  }, [timelineVehicles]);

  return (
    <div className="bs-panel-root">
      <section className="bs-panel-intro">
        <div>
          <p className="bt-section-label">{PAGE_COPY.kicker}</p>
          <h1 className="bs-panel-title">{PAGE_COPY.title}</h1>
          <p className="bs-hero-blurb">{PAGE_COPY.blurb}</p>
          <div className="bs-hero-links">
            <Link className="bs-text-link" to={PAGE_COPY.boostersHref}>
              Browse fleet
            </Link>
            <Link className="bs-text-link" to={PAGE_COPY.statsHref}>
              Fleet stats
            </Link>
          </div>
        </div>
      </section>

      <div className="bs-panels">
        {fleetLoading && !fleetVehicles.length ? (
          <div className="vl-reuse-loading">
            <span className="loading-indicator" />
            <p>Loading booster fleet…</p>
          </div>
        ) : (
          <>
            <BoosterCompare
              fleet={fleetVehicles}
              loading={fleetLoading}
              selectedIds={selectedIds}
              onSelectedIdsChange={handleSelectedIdsChange}
            />
            <ReuseTimelinePanel
              copy={timelineCopy}
              reuseChart={reuseChart}
              reuseView={reuseView}
              setReuseView={setReuseView}
              loading={reuseLoading && !reuseTimeline.length}
              detailPath={PAGE_COPY.detailPath}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BoosterComparePage;
